import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { t } from '../../../../services/i18nService';

import validDragEvents from './validDragEvents';
import GapChoice from './GapChoice';
import LoadTracker from '../../../../services/LoadTracker';

import styles from './gapMatchInteraction.scss';
import constants from '../../../../qti/Constants';
import QuestionRubricBlock from '../../QuestionRubricBlock';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import customFieldsService from '../../../../services/CustomFieldsService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import objectTypes from '../../../../constants/ObjectTypes';
import themeServices from '../../../../themes/ThemeServices';
import UIConstants from '../../../../constants/UIConstants';
import uiReader from '../../../../store/readHelpers/uiReader';
import QuestionDisplayFooter from '../../QuestionDisplayFooter';
import questionReader from '../../../../store/readHelpers/QuestionReader';
import uiService from '../../../../services/UIService';
import GapMatchInteractionServices from '../../../../services/questionView/GapMatchInteractionServices';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import customFieldsConstants from '../../../../constants/CustomFieldsConstants';

let getGapTextsTimeout;

class GapChoicesContainer extends React.Component {
    static propTypes = {
        gapHeight: PropTypes.number,
        gapWidth: PropTypes.number,
        handleUserResponse: PropTypes.func.isRequired,
        model: PropTypes.object.isRequired,
        questionModel: PropTypes.object.isRequired,
        notDropped: PropTypes.array,
        reportReady: PropTypes.func.isRequired,
        isPreRender: PropTypes.bool,
        zoomFactor: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = { dragOver: false };
        this.readyTracker = new LoadTracker(props.reportReady, `GapChoicesContainer ${props.model.identifier}`);
        this.gapChoiceTrackers = props.model.gapChoiceModels
            .reduce((trackers, gapChoiceModel) => {
                trackers[gapChoiceModel.identifier] = this.readyTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
                return trackers;
            }, {});
        this.readyTracker.setAllItemsAdded();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        clearTimeout(getGapTextsTimeout);
    }

    getDraggedGapTextIdentifier = (event) => {
        try {
            const dragData = JSON.parse(event.dataTransfer.getData('text'));
            return dragData.gapTextIdentifier;
        } catch (error) {
            return undefined;
        }
    };

    isValidDragEvent = (event) => {
        return validDragEvents.indexOf(event) !== -1;
    };

    onDragEnter = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
            this.setState({ dragOver: true });
        }
    };

    onDragOver = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
        }
    };

    onDragLeave = (event) => {
        event.preventDefault();
        if (event.target === this.rootElement) {
            this.setState({ dragOver: false });
        }
    };

    onMouseEnter = () => {
        // IA1-3538 Workaround to unlock the gapmatchcontainer if dragLeave didn't get called in SEB
        const { dragOver } = this.state;
        if (dragOver) {
            this.setState({ dragOver: false });
        }
    };

    onDrop = (event) => {
        const gapTextIdentifier = this.getDraggedGapTextIdentifier(event);
        if (gapTextIdentifier) {
            event.preventDefault();
            delete validDragEvents[event.timestamp];
            this.props.handleUserResponse(gapTextIdentifier);
            this.setState({ dragOver: false });
        }
    };

    getGapTextElements = async (remainingRetries = 5) => {
        const gapTextElements = Array.from(
            this.rootElement.getElementsByClassName('gapText')
        );
        if (remainingRetries <= 0 || gapTextElements.length > 0) {
            return Promise.resolve(gapTextElements);
        }
        return new Promise((resolve) => {
            getGapTextsTimeout = setTimeout(() => {
                return resolve(this.getGapTextElements(remainingRetries - 1));
            }, 100);
        });
    };

    getScreenReaderTitle() {
        if (!uiService.areQuestionSectionsFocusable()) {
            return null;
        }

        const questionTitle = scorableItemNumberingService.getQuestionTitle(this.props.questionModel.contentRevisionId);
        return (<span className="sr-only">
            { `${t('generic.tokenAreaForPrefix')} ${questionTitle}` }
        </span>);
    }

    getGapChoiceElements() {
        const { model, notDropped } = this.props;
        const { gapChoiceModels } = model;
        return gapChoiceModels.map((gapChoiceModel, index) => {
            const hidden = !model.reuseAlternatives && notDropped.indexOf(gapChoiceModel) === -1;
            return (
                <GapChoice
                    {...this.props}
                    {...this.state}
                    index={index}
                    gapChoiceModel={gapChoiceModel}
                    key={gapChoiceModel.identifier}
                    hidden={hidden}
                    shouldAutoSizeTokens={model.shouldAutoSizeTokens}
                    extendedRenderingActivated={model.extendedRenderingActivated}
                    reportReady={this.gapChoiceTrackers[gapChoiceModel.identifier]}
                />
            );
        });
    }

    applyPaperBasedTokenPositionStyles({
        wrapperClassNames,
        classNames,
        questionModel,
        tokenPosition,
    }) {
        const isPBRenderRightAndTokenRight =
            settingsReader.isTokenPositionRight(
                questionModel,
                tokenPosition,
                customFieldsConstants.paperBasedRenderOptions.IGM_PIN_RIGHT
            );

        if (isPBRenderRightAndTokenRight) {
            wrapperClassNames.push(styles.pbRightContainer);
            classNames.push(styles.pbChoiceContainer);
        }

        const isPBRenderBelowAndRight =
            settingsReader.isTokenPositionRight(
                questionModel,
                tokenPosition,
                customFieldsConstants.paperBasedRenderOptions.IGM_TOKENS_BELOW
            );

        const isPBRenderBelowAndRightWithSplitPreview =
            settingsReader.isTokenPositionRightWithSplitPreview(
                questionModel,
                tokenPosition,
                customFieldsConstants.paperBasedRenderOptions.IGM_TOKENS_BELOW
            );

        if ((isPBRenderBelowAndRight || isPBRenderBelowAndRightWithSplitPreview) && sessionReader.isPrintStylePaperBased()) {
            wrapperClassNames.push(styles.pbExtendedWrapper);
            classNames.push(styles.pbExtendedChoiceContainer);
        }
    }

    applyPaperBasedClassNames(questionModel, wrapperClassNames, classNames) {
        wrapperClassNames.push(styles.pbTokensWrapper);
        classNames.push(styles.paperBased);
        themeServices.getThemeInstance().addGapChoiceClassNames(questionModel, styles, wrapperClassNames, classNames);

        if (customFieldsService.isMatchingHeadings(questionModel)) {
            wrapperClassNames.push(styles.hasMatchHeadings);
        }
        if (customFieldsService.isMatchingSentenceEnding(questionModel) || customFieldsService.isMatching1(questionModel)) {
            wrapperClassNames.push(styles.hasMatchingStyles);
        }
    }

    getGapChoiceContainerElement() {
        const { model, gapHeight, questionModel, zoomFactor } = this.props;
        const { dragOver } = this.state;
        const isSummaryCompletion = questionModel.customFields ? customFieldsService.isSummaryCompletionObjective(questionModel) : false;
        const displayTokensVertically = uiReader.getThemeVariableValue('question__displayTokensVertically');
        const wrapperClassNames = [styles.gapChoiceContainerWrapper];
        const classNames = [styles.gapChoiceContainer, 'drop'];
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        const isSplitQuestionView = questionReader.isSplitQuestionView(questionModel);
        const { UI_LANGUAGE_OVERRIDE } = window.marketplaceProperties;
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        const tokenPosition = model.tokenPosition || 'top';

        if (isPaperBased) {
            this.applyPaperBasedClassNames(questionModel, wrapperClassNames, classNames);
        }
        if (dragOver) {
            classNames.push('drag-over');
        }
        if ((displayTokensVertically || model.tokenPosition === 'right')
            && !isSummaryCompletion
            && !isPaperBased
        ) {
            classNames.push('vertical');
        }

        let style;
        const printStyle = sessionReader.getPrintParam('printStyle');
        if (model.extendedRenderingActivated) {
            if (printStyle) {
                wrapperClassNames.push(styles.extendedGapChoicesContainerWrapperPrintStyle);
                classNames.push(styles.extendedGapChoiceContainerPrintStyle);
            } else {
                wrapperClassNames.push(styles.extendedGapChoicesContainerWrapper);
                classNames.push(styles.extendedGapChoiceContainer);
            }
        } else {
            style = { minHeight: gapHeight ? `${gapHeight + 12}px` : undefined };
        }

        const multiInteractionsClass = GapMatchInteractionServices
            .getMultiInteractionClass(tokenPosition, zoomFactor, model.shouldAutoSizeTokens);

        if (multiInteractionsClass.length > 0) {
            wrapperClassNames.push(multiInteractionsClass);
        }

        this.applyPaperBasedTokenPositionStyles({
            wrapperClassNames,
            classNames,
            questionModel,
            tokenPosition,
        });

        return (
            <div
                className={wrapperClassNames.join(' ')}
                {...uiService.getQuestionRegionsAccessibilityAttributes()}
            >
                { this.getScreenReaderTitle() }

                { model.tokenHeader && (
                    UI_LANGUAGE_OVERRIDE === 'ceq' ?
                        <h2
                            className={`${styles.tokenHeader} ${styles.tokenHeaderPb}`}
                            dangerouslySetInnerHTML={{ __html: model.tokenHeader }}
                        /> :
                        <h4
                            className={`${styles.tokenHeader} ${styles.tokenHeaderPb}`}
                            dangerouslySetInnerHTML={{ __html: model.tokenHeader }}
                        />
                )}

                <div
                    className={classNames.join(' ')}
                    data-type="drop"
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDragLeave={this.onDragLeave}
                    onMouseEnter={this.onMouseEnter}
                    onDrop={this.onDrop}
                    ref={(element) => { this.rootElement = element; }}
                    style={style}
                >
                    { this.getGapChoiceElements() }

                    { isSplitQuestionView && !isMultipleInteractionsGapMatchQuestion &&
                        <QuestionDisplayFooter
                            isLastQuestionInCurrentNestedSection={false}
                            question={this.props.questionModel}
                            setUserResponse={this.props.handleUserResponse}
                        />
                    }
                </div>
            </div>
        );
    }

    shouldUseGapMatchPortal() {
        const { model, isPreRender } = this.props;
        const { tokenPosition } = model;
        return (
            !isPreRender &&
            tokenPosition === 'right' &&
            this.isGapMatchInStimulus()
        );
    }

    isGapMatchInStimulus() {
        const { model, questionModel } = this.props;
        return (
            model.type === constants.interactionType.GAP_MATCH_INTERACTION &&
            questionModel.category === objectTypes.CATEGORY.STIMULUS
        );
    }

    renderStimulusGapMatchInfo() {
        const useNumberPerInteraction = scorableItemNumberingService.useNumberPerInteraction();
        const { questionModel } = this.props;
        const shouldShowQuestionRubric = uiReader.showQuestionRubric() && questionModel.customFields;

        return (
            <React.Fragment>
                { useNumberPerInteraction &&
                    <h3 className="scorableItemHeadline">
                        {scorableItemNumberingService.getQuestionTitle(questionModel.contentRevisionId)}
                    </h3>
                }
                { shouldShowQuestionRubric && <QuestionRubricBlock question={questionModel} /> }
            </React.Fragment>
        );
    }

    render() {
        const shouldUseGapMatchPortal = this.shouldUseGapMatchPortal();
        const element = document.getElementById(UIConstants.GAP_MATCH_SELECTOR_ID);
        const { questionModel } = this.props;

        if (shouldUseGapMatchPortal && element) {
            return ReactDOM.createPortal(
                <div className={styles.portalContainer} id={`questionPortal-${questionModel.contentRevisionId}`}>
                    {this.renderStimulusGapMatchInfo()}
                    {this.getGapChoiceContainerElement()}
                </div>
                , element
            );
        } else if (sessionReader.isPrintStylePaperBased() && this.isGapMatchInStimulus()) {
            return (
                <React.Fragment>
                    {this.renderStimulusGapMatchInfo()}
                    {this.getGapChoiceContainerElement()}
                </React.Fragment>
            );
        }

        return this.getGapChoiceContainerElement();
    }
}

export default GapChoicesContainer;
